import { useEffect, useState } from "react";
import { X, TrendingUp, Package } from "lucide-react";
import { getProductAnalytics } from "../lib/api";

const fmtUSD = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);

function MiniSparkline({ data, color = "#002FA7" }) {
  const W = 560;
  const H = 120;
  const pad = 6;
  const max = Math.max(1, ...data.map((d) => d.revenue));
  const stepX = (W - pad * 2) / Math.max(1, data.length - 1);
  const points = data.map((d, i) => {
    const x = pad + i * stepX;
    const y = H - pad - (d.revenue / max) * (H - pad * 2);
    return [x, y];
  });
  const path = points.map((p, i) => `${i === 0 ? "M" : "L"} ${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(" ");
  const area = `${path} L ${(pad + (data.length - 1) * stepX).toFixed(1)} ${H - pad} L ${pad} ${H - pad} Z`;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-[120px]" data-testid="product-trend-sparkline">
      <defs>
        <linearGradient id="prodfill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.2" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill="url(#prodfill)" />
      <path d={path} fill="none" stroke={color} strokeWidth="2.2" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

export default function ProductTrendDrawer({ product, range, start, end, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!product) return;
    let alive = true;
    setData(null);
    setErr(null);
    const params = { range };
    if (range === "custom" && start && end) {
      params.start = start;
      params.end = end;
    }
    getProductAnalytics(product.product_id, params)
      .then((d) => alive && setData(d))
      .catch((e) => alive && setErr(e?.response?.data?.detail || e?.message || "Failed to load product analytics"));
    return () => {
      alive = false;
    };
  }, [product, range, start, end]);

  if (!product) return null;

  const peak = data?.trend?.length
    ? data.trend.reduce((max, d) => (d.revenue > max.revenue ? d : max), { date: "", revenue: 0 })
    : null;

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="product-trend-drawer">
      <div className="flex-1 bg-black/50" onClick={onClose} data-testid="product-trend-backdrop" />
      <div className="w-full max-w-2xl bg-white border-l border-zinc-900 shadow-2xl overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-zinc-200 sticky top-0 bg-white z-10">
          <div>
            <div className="micro-label">PRODUCT DRILL-DOWN</div>
            <div className="font-black text-2xl tracking-tight mt-1" style={{ fontFamily: "Outfit" }}>
              {product.name}
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-zinc-100" data-testid="product-trend-close">
            <X size={20} strokeWidth={2.5} />
          </button>
        </div>

        {err && (
          <div className="border-b border-red-200 bg-red-50 p-6 text-red-700 text-sm">{err}</div>
        )}

        {!data && !err && (
          <div className="p-12 micro-label text-center">LOADING PRODUCT DATA...</div>
        )}

        {data && (
          <div className="p-6 space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="border border-zinc-200 p-5" data-testid="product-kpi-revenue">
                <div className="micro-label">PERIOD REVENUE</div>
                <div className="text-3xl font-black tracking-tighter mt-2" style={{ fontFamily: "Outfit" }}>
                  {fmtUSD(data.total_revenue)}
                </div>
                <div className="micro-label mt-1 text-zinc-500">
                  {data.start} → {data.end}
                </div>
              </div>
              <div className="border border-zinc-200 p-5" data-testid="product-kpi-units">
                <div className="micro-label">UNITS SOLD</div>
                <div className="text-3xl font-black tracking-tighter mt-2" style={{ fontFamily: "Outfit" }}>
                  {data.total_units}
                </div>
                {peak && peak.revenue > 0 && (
                  <div className="micro-label mt-1 text-zinc-500 flex items-center gap-1">
                    <TrendingUp size={11} /> peak {peak.date.slice(5)} · {fmtUSD(peak.revenue)}
                  </div>
                )}
              </div>
            </div>

            {/* Trend */}
            <div className="border border-zinc-200 p-5" data-testid="product-trend-card">
              <div className="micro-label mb-3">REVENUE OVER TIME</div>
              <MiniSparkline data={data.trend} />
              <div
                className="grid gap-1 mt-2"
                style={{ gridTemplateColumns: `repeat(${data.trend.length}, minmax(0, 1fr))` }}
              >
                {data.trend.map((d) => (
                  <div key={d.date} className="text-[9px] text-zinc-400 text-center tabular-nums">
                    {d.date.slice(5)}
                  </div>
                ))}
              </div>
            </div>

            {/* Region split */}
            <div className="border border-zinc-200 p-5" data-testid="product-region-split">
              <div className="micro-label mb-4">UNITS BY REGION</div>
              {data.region_split.length === 0 ? (
                <div className="text-center py-6 text-zinc-400">
                  <Package size={32} className="mx-auto mb-2" strokeWidth={2} />
                  <p className="text-sm">No regional sales in this period</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {data.region_split.map((r) => {
                    const pct = data.total_units ? Math.round((r.units / data.total_units) * 100) : 0;
                    return (
                      <div key={r.code} data-testid={`product-region-${r.code}`}>
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-[10px] uppercase tracking-wider font-bold bg-zinc-900 text-white px-2 py-0.5">
                            {r.code}
                          </span>
                          <div className="flex items-center gap-3 tabular-nums">
                            <span className="font-black" style={{ fontFamily: "Outfit" }}>
                              {r.units}
                            </span>
                            <span className="text-xs text-zinc-500 w-10 text-right">{pct}%</span>
                          </div>
                        </div>
                        <div className="h-1.5 bg-zinc-100 overflow-hidden">
                          <div
                            className="h-full klein-bg transition-all duration-500"
                            style={{ width: `${Math.min(100, pct)}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
